import { useContext, useEffect, useState } from 'react';
import { AuthContext } from 'context/AuthContext';

import { Link, useNavigate } from 'react-router-dom';

import { AzureProfile } from 'sso/azure/ui/AzureProfile';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from 'sso/azure/authConfig';
import { getAzureToken } from 'sso/azure/azure-api';
import Button from 'components/ui/button';
import { BiDesktop, BiMobileAlt } from 'react-icons/bi';
import LogoImg from 'assets/img/logo-jj.png';
import ComposicaoFundo from './ComposicaoFundo';
import CamadaDecorativa, { IconeDecorativo } from 'components/camada-decorativa';
import IconeApertoDeMao from 'assets/img/decorativos/aperto-de-mao.svg';
import IconeTimeEquipe from 'assets/img/decorativos/time-equipe.svg';
import IconeInovacao from 'assets/img/decorativos/inovacao.svg';
import IconePromocao from 'assets/img/decorativos/promocao.svg';
import { removeStorageItem, setStorageItem } from 'utils/storage';
import { getRegulation } from 'services/regulation.service';
import { SAIDA_DELIBERADA } from 'utils/constants';

// Camada do cartão de entrada, a 16% no design.
const DECORACAO_LOGIN: IconeDecorativo[] = [
  { src: IconeApertoDeMao, tamanho: 54, estilo: { left: '4%', top: '6%', transform: 'rotate(-12deg)' } },
  { src: IconeTimeEquipe, tamanho: 58, estilo: { right: '5%', top: '10%', transform: 'rotate(14deg)' } },
  { src: IconeInovacao, tamanho: 46, estilo: { left: '8%', bottom: '8%', transform: 'rotate(9deg)' } },
  { src: IconePromocao, tamanho: 50, estilo: { right: '7%', bottom: '6%', transform: 'rotate(-8deg)' } },
];

export function Login() {
  const [saiu, setSaiu] = useState(false);
  const navigate = useNavigate();
  const { instance } = useMsal();
  const [loading, setLoading] = useState(false);
  const [invalidUser, setInvalidUser] = useState(false);
  const { loginWithToken, login } = useContext(AuthContext);
  const [useSSO, setUseSSO] = useState(false);
  const [rulesUrl, setRulesUrl] = useState<string>();

  // function handleLogin() {
  //   window.location.href = 'https://bb.funifier.com';
  // }

  // function handleLoginExterno() {
  //   window.location.href = 'https://bb-externo.funifier.com';
  // }

  function loginSSO() {
    setLoading(true);
    setUseSSO(true);
    setInvalidUser(false);
  }

  async function handleLoginSSO(response: any) {
    // console.log('handleLoginSSO', response);

    try {
      const password = await getAzureToken();
      const username = response.userPrincipalName;

      if (username && password) {
        await login({ username, password });
        navigate('/');
      }
    } catch (e) {
      setLoading(false);
      setInvalidUser(true);
      setUseSSO(false);
    }
  }

  useEffect(() => {
    async function doLogin(username: string, password: string) {
      try {
        await login({ username, password });
        navigate('/');
      } catch (e) {
        setLoading(false);
        setInvalidUser(true);
      }
    }

    async function doLoginWithToken(token: string) {
      try {
        await loginWithToken(token);
        navigate('/');
      } catch (e) {
        setLoading(false);
        setInvalidUser(true);
      }
    }

    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    const agent = params.get('agent');
    const secret = params.get('secret');
    const tokenItem = params.get('tokenItem');

    if (token) {
      setLoading(true);
      doLoginWithToken(token);
      removeStorageItem('readOnly');
    } else if (agent && secret) {
      setLoading(true);
      doLogin(agent, secret);
      removeStorageItem('readOnly');
    } else if (tokenItem) {
      setLoading(true);
      const decodedToken = atob(tokenItem);
      const tokenParts = decodedToken.split(':');
      const id = tokenParts[0];
      const password = tokenParts[1];
      const readOnly = tokenParts[2];

      if (readOnly === 'readOnly') {
        setStorageItem('readOnly', 'true');
      }

      doLogin(id, password);
    } else {
      // setUseSSO(true);
      setInvalidUser(true);
    }
  }, [loginWithToken, login, navigate]);

  // O fundo da entrada é liso, sem a arte do topo — ver App.scss.
  useEffect(() => {
    document.body.classList.add('bg-login');

    return () => document.body.classList.remove('bg-login');
  }, []);

  useEffect(() => {
    if (sessionStorage.getItem(SAIDA_DELIBERADA)) {
      sessionStorage.removeItem(SAIDA_DELIBERADA);
      setSaiu(true);
      return;
    }

    const lerHash = () => setSaiu((window.location.hash || '') === '#sair');

    lerHash();
    window.addEventListener('hashchange', lerHash);

    return () => window.removeEventListener('hashchange', lerHash);
  }, []);

  useEffect(() => {
    async function loadData() {
      const regulation = await getRegulation();
      setRulesUrl(regulation?.url);
    }

    if (!rulesUrl) loadData();
  }, [rulesUrl]);

  return (
    <div className="relative flex min-h-screen items-center justify-center px-6 py-10">
      <ComposicaoFundo />

      <div className="jj-rise relative z-10 flex w-full max-w-[560px] flex-col items-center gap-4">
        {saiu && (
          <div className="inline-flex items-center gap-2.5 rounded-xl bg-brand-white px-[18px] py-3 text-[13px] text-content shadow-jj-01">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="var(--brand-blue)"
              strokeWidth="2.4"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
              focusable="false"
            >
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <path d="m16 17 5-5-5-5" />
              <path d="M21 12H9" />
            </svg>
            Você saiu do <b className="font-display text-brand-blue">#JogaJuntoBB</b>
          </div>
        )}
        <div className="relative flex w-full flex-col items-center gap-[22px] overflow-hidden rounded-2xl bg-brand-blue px-11 pb-[42px] pt-10 text-center shadow-jj-01">
          <CamadaDecorativa icones={DECORACAO_LOGIN} opacidade={0.16} emBranco />

          <img src={LogoImg} alt="Joga Junto BB" className="relative block w-[min(260px,70vw)]" />

          <p className="relative m-0 font-display text-[23px] font-bold leading-[1.15] text-brand-yellow">
            Uma vida de
            <br />
            oportunidades!
          </p>

          <div className="relative flex w-full flex-col items-center gap-3.5 rounded-xl bg-brand-white px-[26px] pb-[26px] pt-6 shadow-jj-01">
            <p className="m-0 font-display text-[19px] font-bold leading-tight text-brand-blue-dark">
              Juntos fazemos muito mais!
            </p>

            <span className="h-px w-full bg-[#E5E7EB]" />

            <p className="m-0 max-w-[46ch] text-[13px] leading-relaxed text-content">
              A participação no <b className="font-display text-brand-blue">#JogaJuntoBB</b> é voluntária.
            </p>

            <p className="m-0 max-w-[48ch] text-[13px] leading-relaxed text-content">
              Para participar leia o{' '}
              <a href={rulesUrl} target="_blank" rel="noreferrer" className="font-semibold text-brand-blue underline">
                regulamento
              </a>{' '}
              e manifeste sua ciência e concordância com os termos e condições da ação.
            </p>

            {loading && !useSSO && !invalidUser && (
              <div className="mt-1.5 w-full rounded-md bg-brand-yellow px-6 py-[15px] text-center font-text text-xs font-bold uppercase tracking-[0.05em] text-brand-blue">
                Carregando...
              </div>
            )}

            {!loading && invalidUser && (
              <button
                type="button"
                onClick={() => {
                  instance.loginPopup(loginRequest);
                  setTimeout(() => {
                    loginSSO();
                  }, 200);
                }}
                className="jj-entrar mt-1.5 flex w-full items-center justify-center gap-[11px] rounded-md border-none bg-brand-yellow px-6 py-[15px] font-text text-xs font-bold uppercase tracking-[0.05em] text-brand-blue"
              >
                <BiDesktop size={18} aria-hidden="true" />
                {saiu ? 'Entrar novamente' : 'Clique para entrar'}
              </button>
            )}
          </div>
        </div>

        <p className="m-0 max-w-[52ch] text-center text-[13px] leading-relaxed text-content-soft">
          Acesso pela rede interna do Banco do Brasil. Em caso de dúvida, consulte as{' '}
          <a href="/ajuda" className="text-brand-blue underline">
            dúvidas frequentes
          </a>
          .
        </p>

        {useSSO ? <AzureProfile onAuthenticate={handleLoginSSO} /> : null}
      </div>
    </div>
  );
}
